import { ConversationStatus, ConversationSummaryDTO } from 'models/conversations'

import { PageableResponse } from 'models/misc'
import { resolveExpectedParticipantRole } from './resolveExpectedRole'
import { useAuth0 } from 'utils/auth'
import { useGetConversations } from 'dataQueries/conversations.query'
import { useMemo } from 'react'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * Resolves the platform UUID of the caller for the purposes of comparing against a message's
 * `senderId`.
 *
 * We *prefer* the participant-derived id over `baseUserData.internalId` because the participants
 * entries are written by the same backend persistence path that stamps `senderId` on messages —
 * they are guaranteed to agree. `baseUserData.internalId`, on the other hand, comes from `/user/me`
 * which can disagree (e.g. an older cached payload, a role flow that doesn't populate it) and
 * silently break the outgoing/incoming split. Only fall back to it if the participants list isn't
 * in cache yet, which would only happen if the widget is rendered before the list has loaded.
 */
export const useCurrentParticipantId = (conversationId: string | null | undefined): string | undefined => {
  const { roles } = useAuth0()
  const { baseUserData } = useUserData()
  // We probe both list caches (no-filter at the ChatWidget level, ACTIVE-filtered inside the
  // popover) so we hit whichever has been mounted in the current widget flow.
  const { data: pageAny } = useGetConversations()
  const { data: pageActive } = useGetConversations(ConversationStatus.ACTIVE)

  return useMemo(() => {
    const expectedRole = resolveExpectedParticipantRole(baseUserData?.platformRole, roles)

    const findIn = (page: PageableResponse<ConversationSummaryDTO[]> | undefined) =>
      conversationId ? page?.content.find((c) => c.id === conversationId) : undefined
    const conversation = findIn(pageAny) ?? findIn(pageActive)

    const fromParticipants = expectedRole && conversation
      ? conversation.participants.find((p) => p.role === expectedRole)?.userId
      : undefined

    return fromParticipants ?? baseUserData?.internalId
  }, [baseUserData?.internalId, baseUserData?.platformRole, conversationId, pageAny, pageActive, roles])
}
